import { Alert } from "@/core/components/ui/alert/alert";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";

interface SubmissionConfirmModalProps {
  open: boolean;
  isSaving: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

// Saying "I have sent everything" puts a person to work on it, so it is worth one
// deliberate step. The modal is also the honest place for the caveat: complete enough is
// our judgement, not theirs, and we may come back for more.
export const SubmissionConfirmModal = ({
  open,
  isSaving,
  error,
  onCancel,
  onConfirm,
}: SubmissionConfirmModalProps) => (
  <Dialog
    open={open}
    onOpenChange={(next) => {
      // Dismissing mid-save would hide the outcome, so hold the modal open.
      if (!next && !isSaving) onCancel();
    }}
  >
    <DialogContent description="Send your documents for review. Volca builds a first version of your payroll from them.">
      <DialogHeader>
        <DialogTitle>Send everything for review?</DialogTitle>
      </DialogHeader>

      <Stack className="p-6" gap="md" fullWidth align="stretch">
        <Typography variant="bodyW1" textColor="secondary">
          We will build a first version of your payroll from what you sent. That
          usually takes a few days.
        </Typography>
        <Typography variant="bodyW1" textColor="secondary">
          If something is missing, or we cannot follow how a number was worked
          out, we will come back and ask you for more. That is normal, and it is
          quicker than us guessing.
        </Typography>
        <Typography variant="captionW1" textColor="tertiary">
          If you find more later, you can reopen these steps and send it.
        </Typography>
        {error && <Alert variant="destructive">{error}</Alert>}
      </Stack>

      <DialogFooter>
        <Button
          variant="secondary"
          size="sm"
          type="button"
          disabled={isSaving}
          onClick={onCancel}
        >
          Not yet
        </Button>
        <Button
          size="sm"
          type="button"
          loading={isSaving}
          disabled={isSaving}
          onClick={onConfirm}
        >
          Send for review
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
